// Pure layout for the Tiempo map: dot separation, pill placement and desktop label placement (SVG units, VIEW 1000x550)
export const DOT_R = 24;
export const PILL_H = 46;
export const PILL_FONT = 30;
export const PILL_ICON = 28;
export const PILL_PAD = 14;
export const HIT = 118; // ≈44pt at 375pt wide
const GAP = 8;
const SIDES = ['e', 'o', 'ne', 'no', 'se', 'so', 'n', 's'];

export const pillWidth = (temp, withIcon) => `${temp}°`.length * 17 + PILL_PAD * 2 + (withIcon ? PILL_ICON + 6 : 0);
export const overlaps = (a, b, m = 0) => a.x < b.x + b.w + m && a.x + a.w + m > b.x && a.y < b.y + b.h + m && a.y + a.h + m > b.y;
const dotRect = (p, r) => ({ x: p.x - r, y: p.y - r, w: r * 2, h: r * 2 });

const rectForSide = (side, p, w, h, d, view) => {
  const o = DOT_R + GAP + d;
  const c = { e: [o, 0], o: [-o, 0], ne: [o * 0.8, -o * 0.8], no: [-o * 0.8, -o * 0.8], se: [o * 0.8, o * 0.8], so: [-o * 0.8, o * 0.8], n: [0, -o], s: [0, o] }[side];
  let x, y;
  if (side === 'e' || side === 'ne' || side === 'se') x = p.x + c[0];
  else if (side === 'o' || side === 'no' || side === 'so') x = p.x + c[0] - w;
  else x = p.x - w / 2;
  if (side === 'n' || side === 'ne' || side === 'no') y = p.y + c[1] - h;
  else if (side === 's' || side === 'se' || side === 'so') y = p.y + c[1];
  else y = p.y - h / 2;
  x = Math.max(4, Math.min(view.w - w - 4, x));
  y = Math.max(4, Math.min(view.h - h - 4, y));
  return { x, y, w, h };
};

// Dense zones: push points apart up to ~4pt (11 units) so dots never touch
export const separate = (pts) => {
  const out = pts.map((p) => ({ ...p, ox: p.x, oy: p.y }));
  const maxShift = 11, minDist = DOT_R * 2 + 6;
  for (let iter = 0; iter < 6; iter++) {
    for (let i = 0; i < out.length; i++) for (let j = i + 1; j < out.length; j++) {
      const a = out[i], b = out[j];
      const dx = b.x - a.x, dy = b.y - a.y, d = Math.hypot(dx, dy) || 0.01;
      if (d >= minDist) continue;
      const push = (minDist - d) / 2, ux = dx / d, uy = dy / d;
      [[a, -1], [b, 1]].forEach(([p, s]) => {
        const nx = p.x + s * ux * push, ny = p.y + s * uy * push;
        const sx = nx - p.ox, sy = ny - p.oy, sd = Math.hypot(sx, sy);
        if (sd <= maxShift) { p.x = nx; p.y = ny; } else { p.x = p.ox + (sx / sd) * maxShift; p.y = p.oy + (sy / sd) * maxShift; }
      });
    }
  }
  return out;
};

/**
 * items: [{ id, name, x, y, view: { state, showPill, temp, posicion } }]
 * returns items with x/y separated and `pill` / `label` rects attached
 */
export function layoutPoints(items, { view, withIcon = true } = {}) {
  const pts = separate(items);
  const blockers = pts.map((p) => dotRect(p, DOT_R + 11)); // ring (DOT_R+9.5) never covered, stable across selection
  const placed = [];
  pts.forEach((p, idx) => {
    if (!p.view?.showPill || !p.view.state) return;
    const w = pillWidth(p.view.temp, withIcon);
    const order = p.view.posicion ? [p.view.posicion, ...SIDES.filter((s) => s !== p.view.posicion)] : SIDES;
    let best = null, bestScore = Infinity;
    for (const d of [0, 20, 40, 60, 90, 120]) {
      for (const side of order) {
        const r = rectForSide(side, p, w, PILL_H, d, view);
        let score = d / 100;
        blockers.forEach((b, i) => { if (i !== idx && overlaps(r, b)) score += 10; });
        placed.forEach((q) => { if (overlaps(r, q.rect, 4)) score += 5; });
        if (score < bestScore) { bestScore = score; best = { ...r, side, d }; }
        if (score === 0) break;
      }
      if (bestScore === 0) break;
    }
    p.pill = best;
    placed.push({ rect: best });
  });
  const labels = [];
  pts.forEach((p) => {
    const w = (p.name || '').length * 11.5 + 8, h = 24;
    const cands = [
      { x: p.x - w / 2, y: p.y + DOT_R + 8, w, h, anchor: 'middle', tx: p.x, ty: p.y + DOT_R + 26 },
      { x: p.x - w / 2, y: p.y - DOT_R - 8 - h, w, h, anchor: 'middle', tx: p.x, ty: p.y - DOT_R - 12 },
      { x: p.x + DOT_R + 10, y: p.y - h / 2, w, h, anchor: 'start', tx: p.x + DOT_R + 10, ty: p.y + 7 },
      { x: p.x - DOT_R - 10 - w, y: p.y - h / 2, w, h, anchor: 'end', tx: p.x - DOT_R - 10, ty: p.y + 7 },
    ];
    let best = cands[0], bestScore = Infinity;
    cands.forEach((c, i) => {
      let score = i * 0.1;
      blockers.forEach((b) => { if (overlaps(c, b)) score += 10; });
      placed.forEach((q) => { if (overlaps(c, q.rect, 2)) score += 10; });
      labels.forEach((l) => { if (overlaps(c, l, 2)) score += 6; });
      if (c.x < 0 || c.x + c.w > view.w) score += 20;
      if (score < bestScore) { bestScore = score; best = c; }
    });
    p.label = best;
    labels.push(best);
  });
  return pts;
}

// Diagnostics used by tests: pill/pill, pill/dot and dot/dot collisions
export function layoutIssues(pts) {
  const issues = [];
  const dots = pts.map((p) => ({ id: p.id, ...dotRect(p, DOT_R) }));
  pts.forEach((p, i) => {
    pts.slice(i + 1).forEach((q) => {
      if (Math.hypot(p.x - q.x, p.y - q.y) < DOT_R * 2) issues.push(`dots touch: ${p.id} / ${q.id}`);
      if (p.pill && q.pill && overlaps(p.pill, q.pill)) issues.push(`pills overlap: ${p.id} / ${q.id}`);
    });
    if (p.pill) dots.forEach((d) => { if (d.id !== p.id && overlaps(p.pill, d)) issues.push(`pill ${p.id} covers dot ${d.id}`); });
  });
  return issues;
}
